import React from "react"; 
import { useAuth } from "../context/UserContext";
import SidebarPanel from "./SidebarPanel";
import { FaBrain, FaQuestionCircle, FaClipboardList } from "react-icons/fa";
import { HiOutlineAcademicCap, HiOutlineBriefcase, HiOutlineUserGroup, HiOutlineSparkles } from "react-icons/hi";
import { Profileinfo } from "./Layout/Profileinfo";
import { Link } from "react-router-dom";
import { Footer } from "./Layout/Footer";

const Dashboard = () => {
  const { user } = useAuth(); 

  return (
    <div className="flex h-screen"> 
      <SidebarPanel className="w-64 fixed top-0 left-0 h-full bg-primary shadow-lg" /> 
      <div className="flex-1 ml-64 overflow-y-auto p-6 bg-gray-50"> 
        <header className="flex justify-between items-center mb-6 border-b pb-4 dashboard_header">
          <h1 className="text-2xl text-primary font-bold">
            Welcome to the <span className="text-black">Quiz Maker AI</span>
          </h1>
          {user && <Profileinfo />}
        </header>
        
        {/* Hero Section */}
        <main className="text-center main_wrapper">
          <section className="hero_section">
            <h2 className="text-5xl font-extrabold text-primary mb-6 leading-tight">
              Redefine Engagement with <span className="text-black">Quiz Maker AI</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Transform the way you learn, teach, or train with our intuitive and powerful quiz creation platform.
              Whether you're an educator, corporate trainer, or quiz enthusiast, Quiz Maker AI provides the tools you need
              to captivate your audience and measure success effectively.
            </p>
            <div className="mt-8">
              <button className="px-8 py-3 bg-gradient-to-r from-blueStart to-blueEnd text-white text-lg font-semibold rounded-lg shadow-lg hover:from-hoverBlueStart hover:to-hoverBlueEnd">
                <Link to="/multi-choice">Create Your First Quiz Now</Link> 
              </button>
            </div>
          </section>
          
          {/* Features Section */}
          <section className="mt-12 bg-gray-800 text-white py-12 px-6 rounded-lg">
            <h2 className="text-3xl font-semibold mb-6">
              Features of Quiz Maker AI
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaQuestionCircle className="text-blue-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Multiple Choice</h3>
                <p>Design quizzes with multiple-choice questions to test knowledge effectively.</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaBrain className="text-yellow-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">True/False</h3>
                <p>Quickly assess understanding with true or false statements.</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaClipboardList className="text-green-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Short Answer</h3>
                <p>Capture open-ended responses for deeper insights.</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaQuestionCircle className="text-pink-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Fill-in-the-Blank</h3>
                <p>Encourage recall with fill-in-the-blank style questions.</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaBrain className="text-indigo-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Matching</h3>
                <p>Use matching pairs to test relationships and memory.</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaClipboardList className="text-red-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Essay</h3>
                <p>Encourage detailed explanations with essay questions.</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaQuestionCircle className="text-cyan-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Ranking/Ordering</h3>
                <p>Ask users to arrange items in a specific order.</p>
              </div>
              <div className="p-6 bg-gray-900 rounded-lg shadow-md">
                <FaBrain className="text-orange-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Numerical Response</h3>
                <p>Collect numerical answers to evaluate calculations or measurements.</p>
              </div>
            </div>
          </section>
           
          <section className="mt-16">
            <h2 className="text-5xl font-extrabold text-primary mb-6 leading-tight">
              Use <span className="text-black">Cases</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-6 bg-white shadow-md rounded-lg text-center">
                <HiOutlineAcademicCap className="text-green-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Educational Institutions</h3>
                <p>Engage students with interactive quizzes to improve learning outcomes.</p>
              </div>
              <div className="p-6 bg-white shadow-md rounded-lg text-center">
                <HiOutlineBriefcase className="text-yellow-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Corporate Training</h3>
                <p>Train employees with customized quizzes to assess knowledge retention.</p>
              </div>
              <div className="p-6 bg-white shadow-md rounded-lg text-center">
                <HiOutlineUserGroup className="text-purple-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Team Building Activities</h3>
                <p>Use fun quizzes to improve team morale during corporate events.</p>
              </div>
              <div className="p-6 bg-white shadow-md rounded-lg text-center">
                <HiOutlineSparkles className="text-pink-500 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Personal Use</h3>
                <p>Host trivia nights or create quizzes for personal enrichment and fun.</p>
              </div>
            </div>
          </section> 
          <Footer/>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;